import React from 'react';
import { Chip, Box, useTheme } from '@mui/material';
import {
  LocalFireDepartment,
  AcUnit,
  RemoveCircleOutline
} from '@mui/icons-material';
import { LeadQuality } from '../types';

interface LeadQualityIconProps {
  quality?: LeadQuality;
  size?: 'small' | 'medium';
  transparent?: boolean;
  minimal?: boolean;
}

const LeadQualityIcon: React.FC<LeadQualityIconProps> = ({ quality, size = 'medium', transparent = false, minimal = false }) => {
  const theme = useTheme();

  const getQualityConfig = (quality?: LeadQuality) => {
    const isDark = theme.palette.mode === 'dark';

    switch (quality) {
      case 'Quente':
        return {
          icon: <LocalFireDepartment />,
          label: '',
          color: isDark ? '#f87171' : '#f44336',
          bgColor: isDark ? 'rgba(248, 113, 113, 0.1)' : '#ffebee'
        };
      case 'Frio':
        return {
          icon: <AcUnit />,
          label: '',
          color: isDark ? '#65f0c5' : '#2196f3',
          bgColor: isDark ? 'rgba(96, 165, 250, 0.1)' : '#e3f2fd'
        };
      case 'Neutro':
      default:
        return {
          icon: <RemoveCircleOutline />,
          label: '',
          color: isDark ? '#fbbf24' : '#ff9800',
          bgColor: isDark ? 'rgba(251, 191, 36, 0.1)' : '#fff3e0'
        };
    }
  };

  const config = getQualityConfig(quality);

  if (minimal) {
    return (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: config.color,
          '& svg': {
            fontSize: size === 'small' ? 18 : 22,
          },
        }}
      >
        {config.icon}
      </Box>
    );
  }

  return (
    <Chip
      icon={config.icon}
      size={size}
      sx={{
        color: config.color,
        backgroundColor: transparent ? 'transparent' : config.bgColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: size === 'small' ? '28px' : '36px',
        height: size === 'small' ? '20px' : '28px',
        padding: 0,
        boxShadow: 'none',
        border: 'none',
        '& .MuiChip-label': {
          display: 'none', // Esconde o label para todos os tipos
        },
        '& .MuiChip-icon': {
          color: config.color,
          margin: 0,
          padding: 0,
          fontSize: size === 'small' ? '16px' : '20px',
          // Animação de piscamento para leads quentes
          ...(!transparent && quality === 'Quente' && {
            animation: 'fireBlink 1.5s ease-in-out infinite',
            '@keyframes fireBlink': {
              '0%, 100%': {
                opacity: 1,
                transform: 'scale(1)',
                filter: 'brightness(1)',
              },
              '25%': {
                opacity: 0.7,
                transform: 'scale(1.1)',
                filter: 'brightness(1.3)',
              },
              '50%': {
                opacity: 0.9,
                transform: 'scale(0.95)',
                filter: 'brightness(0.8)',
              },
              '75%': {
                opacity: 0.8,
                transform: 'scale(1.05)',
                filter: 'brightness(1.2)',
              },
            },
          }),
        },
        // Efeito adicional para o chip inteiro quando for quente
        ...(!transparent && quality === 'Quente' && {
          animation: 'cardGlow 2s ease-in-out infinite',
          '@keyframes cardGlow': {
            '0%, 100%': {
              boxShadow: theme.palette.mode === 'dark'
                ? '0 2px 4px rgba(248, 113, 113, 0.3)'
                : '0 2px 4px rgba(244, 67, 54, 0.2)',
            },
            '50%': {
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 8px rgba(248, 113, 113, 0.5)'
                : '0 4px 8px rgba(244, 67, 54, 0.4)',
            },
          },
        }),
      }}
    />
  );
};

export default LeadQualityIcon;